import React, { useEffect, useState } from 'react';
import {
  Box,
  Center,
  VStack,
  Spinner,
  Text,
  Alert,
  AlertIcon,
  Button,
  useColorModeValue,
} from '@chakra-ui/react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app, auth } from '../utils/init-firebase';

export default function OAuthCallback() {
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState(null);
  const bgColor = useColorModeValue('white', 'gray.800');

  useEffect(() => {
    handleOAuthCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOAuthCallback = async () => {
    try {
      // Wait for Firebase auth to initialize
      await new Promise((resolve) => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
          unsubscribe();
          resolve(user);
        });
      });

      // Extract code from URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state'); // User ID
      const errorParam = urlParams.get('error');

      if (errorParam) {
        setStatus('error');
        setError(`OAuth error: ${errorParam}`);
        
        // Send message to parent window
        if (window.opener) {
          window.opener.postMessage(
            { type: 'oauth_error', error: errorParam },
            window.location.origin
          );
        }
        
        // Auto-close after delay
        setTimeout(() => {
          window.close();
        }, 3000);
        return;
      }

      if (!code) {
        setStatus('error');
        setError('No authorization code received');
        
        if (window.opener) {
          window.opener.postMessage(
            { type: 'oauth_error', error: 'No authorization code' },
            window.location.origin
          );
        }
        
        setTimeout(() => window.close(), 3000);
        return;
      }

      // Call backend to exchange code for tokens. The callable only does the
      // fast connect work (token + watch channel); the event import happens
      // afterwards in the background, so this returns in seconds.
      const functions = getFunctions(app, 'us-east1');
      const handleCallback = httpsCallable(functions, 'handleCalendarOAuthCallback', { timeout: 120000 });

      const result = await handleCallback({
        code,
        redirectUri: `${window.location.origin}/oauth/calendar/callback`,
      });

      if (result.data.success) {
        reportSuccess();
      } else {
        setStatus('error');
        setError(result.data.message || 'Failed to connect');
        setTimeout(() => window.close(), 3000);
      }
    } catch (err) {
      console.error('OAuth callback error:', err);

      // Before showing a failure, check whether the connection actually went
      // through -- a client-side timeout or dropped response does NOT mean the
      // server failed (it keeps running and usually succeeds).
      const connected = await verifyActuallyConnected();
      if (connected) {
        reportSuccess();
        return;
      }

      setStatus('error');
      setError(err.message || 'Connection failed');

      if (window.opener) {
        window.opener.postMessage(
          { type: 'oauth_error', error: err.message },
          window.location.origin
        );
      }

      setTimeout(() => window.close(), 3000);
    }
  };

  const reportSuccess = () => {
    setStatus('success');

    if (window.opener) {
      window.opener.postMessage(
        { type: 'oauth_success', message: 'Calendar connected' },
        window.location.origin
      );
    }

    setTimeout(() => {
      window.close();
    }, 1500);
  };

  const verifyActuallyConnected = async () => {
    try {
      const functions = getFunctions(app, 'us-east1');
      const getStatus = httpsCallable(functions, 'getCalendarSyncStatus');
      const result = await getStatus({});
      return result.data?.connected === true;
    } catch (_) {
      return false;
    }
  };

  return (
    <Box bg={bgColor} minH="100vh">
      <Center h="100vh">
        <VStack spacing={6}>
          {status === 'processing' && (
            <>
              <Spinner size="xl" color="blue.500" thickness="4px" />
              <Text fontSize="lg" fontWeight="medium">
                Connecting to Google Calendar...
              </Text>
              <Text fontSize="sm" color="gray.600">
                Please wait while we complete the setup
              </Text>
            </>
          )}

          {status === 'success' && (
            <>
              <Alert status="success" variant="subtle" borderRadius="lg">
                <AlertIcon />
                <VStack align="flex-start" spacing={1}>
                  <Text fontWeight="semibold">
                    Calendar Connected Successfully!
                  </Text>
                  <Text fontSize="sm">
                    This window will close automatically...
                  </Text>
                </VStack>
              </Alert>
            </>
          )}

          {status === 'error' && (
            <>
              <Alert status="error" variant="subtle" borderRadius="lg">
                <AlertIcon />
                <VStack align="flex-start" spacing={1}>
                  <Text fontWeight="semibold">Connection Failed</Text>
                  <Text fontSize="sm">{error}</Text>
                </VStack>
              </Alert>
              <Button size="sm" onClick={() => window.close()}>
                Close Window
              </Button>
            </>
          )}
        </VStack>
      </Center>
    </Box>
  );
}

